import type { AppConfig } from "../../types/appConfig";
import type { Company } from "../auth/Auth.typed";
import { assertResponseOk, fetchZekay, fetchZekayJson } from "../zekay/httpClient";

function readString(raw: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string") return v;
    if (v != null && typeof v !== "object") return String(v);
  }
  return undefined;
}

function readBool(raw: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "boolean") return v;
  }
  return undefined;
}

export function normalizeCompanyResponse(raw: Record<string, unknown>): Company {
  const idRaw = raw.id ?? raw.ID;
  const idNum = typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);

  return {
    id: Number.isFinite(idNum) ? idNum : 0,
    name: readString(raw, "name", "Name"),
    phone: readString(raw, "phone", "Phone"),
    address: readString(raw, "address", "Address"),
    city: readString(raw, "city", "City"),
    postal_code: readString(raw, "postal_code", "PostalCode"),
    country: readString(raw, "country", "Country"),
    is_active: readBool(raw, "is_active", "IsActive"),
    mail_address: readString(raw, "mail_address", "MailAddress"),
    ui_theme_preset: readString(raw, "ui_theme_preset", "UiThemePreset", "UIThemePreset"),
    siret: readString(raw, "siret", "Siret"),
    ape: readString(raw, "ape", "Ape"),
  };
}

function companyToWire(comp: Company): Record<string, unknown> {
  return {
    id: comp.id,
    name: comp.name ?? "",
    phone: comp.phone ?? "",
    address: comp.address ?? "",
    city: comp.city ?? "",
    postal_code: comp.postal_code ?? "",
    country: comp.country ?? "",
    is_active: comp.is_active ?? true,
    mail_address: comp.mail_address ?? "",
    ui_theme_preset: comp.ui_theme_preset ?? "",
    siret: comp.siret ?? "",
    ape: comp.ape ?? "",
  };
}

export class CompanyModel {
  zekayAPI: string;

  constructor(cfg: AppConfig) {
    this.zekayAPI = cfg.zekayAPI;
  }

  async getCurrentCompany(): Promise<Company> {
    const raw = await fetchZekayJson<Record<string, unknown>>(`${this.zekayAPI}/company/current`);

    return normalizeCompanyResponse(raw);
  }

  async updateCompany(comp: Company): Promise<Company> {
    const res = await fetchZekay(`${this.zekayAPI}/companies/${comp.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(companyToWire(comp)),
    });
    await assertResponseOk(res);
    const raw = (await res.json()) as Record<string, unknown>;

    return normalizeCompanyResponse(raw);
  }
}
